// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
/**
 * game.ts — the whole of Rootbound's rules, as pure functions over a plain state.
 *
 * Nothing here touches the DOM, the network, the clock or `Math.random`. That is
 * deliberate and load-bearing three times over:
 *
 *  - **Lockstep P2P.** Play has no randomness and no hidden information, so a
 *    turn is fully described by an Action. Both peers re-simulate it through
 *    THIS file. No board state ever crosses the wire, so the classic desync
 *    surface does not exist — a peer handed an illegal action rejects it
 *    instead of corrupting itself.
 *  - **The AI** searches by applying and unapplying the same functions a human
 *    move goes through, so the bot can never do something a player cannot.
 *  - **The balance sim** (principle #18) plays hundreds of games a second here,
 *    which is only possible because a position is a Map and a couple of ints.
 */

import {
  add,
  cutCells,
  DIRS,
  gateCells,
  type Hex,
  type HexKey,
  isConnected,
  key,
  neighborKeys,
  parseKey,
} from './hex';
import { type Hand, handSize, type Mode, type PieceType, PIECE_ORDER } from './modes';

export type Seat = 0 | 1;

export interface Piece {
  type: PieceType;
  owner: Seat;
}

export type Action =
  | { k: 'place'; piece: PieceType; to: Hex; convert?: Hex }
  | { k: 'move'; from: Hex; to: Hex }
  | { k: 'pass' };

export type EndReason = 'ring' | 'pressure' | 'stalemate' | 'resign';

export interface GameState {
  mode: Mode;
  cells: Map<HexKey, Piece>;
  hands: [Hand, Hand];
  toMove: Seat;
  /** Half-moves played. Also the tempo clock the turn cap counts. */
  ply: number;
  over: boolean;
  /** The winner, or null for a draw. Only meaningful once `over`. */
  winner: Seat | null;
  reason: EndReason | null;
  /** Consecutive passes. Two in a row means neither side can act. */
  passes: number;
  history: Action[];
}

/**
 * Polarity — the signature rule.
 *
 * A piece surrounded by its own colour is *nested*: supported, safe, and it
 * shuffles one cell. A piece outnumbered by the enemy is *reaching*: exposed,
 * unsupported, and it moves its full range. Ties resolve to nested, so a piece
 * has to be genuinely outnumbered to earn the boost — being merely in contact
 * is not "reaching into enemy territory".
 */
export type Polarity = 'nested' | 'reaching';

interface Gait {
  nested: number;
  reaching: number;
  /** Must the slide use its full length, or may it stop short? */
  exact: boolean;
  /** Ignore the squeeze rule — the Burrower's entire identity. */
  squeeze: boolean;
}

export const GAITS: Record<'creeper' | 'burrower' | 'warden', Gait> = {
  creeper: { nested: 1, reaching: 3, exact: true, squeeze: false },
  burrower: { nested: 1, reaching: 2, exact: false, squeeze: true },
  warden: { nested: 1, reaching: 2, exact: true, squeeze: false },
};

export const ORIGIN: Hex = { q: 0, r: 0 };

export function createGame(mode: Mode): GameState {
  return {
    mode,
    cells: new Map(),
    hands: [{ ...mode.hand }, { ...mode.hand }],
    toMove: 0,
    ply: 0,
    over: false,
    winner: null,
    reason: null,
    passes: 0,
    history: [],
  };
}

export function cloneState(s: GameState): GameState {
  return {
    mode: s.mode,
    cells: new Map(s.cells),
    hands: [{ ...s.hands[0] }, { ...s.hands[1] }],
    toMove: s.toMove,
    ply: s.ply,
    over: s.over,
    winner: s.winner,
    reason: s.reason,
    passes: s.passes,
    history: s.history.slice(),
  };
}

export const other = (seat: Seat): Seat => (seat === 0 ? 1 : 0);

export function occupiedSet(s: GameState): Set<HexKey> {
  return new Set(s.cells.keys());
}

export function heartroot(s: GameState, seat: Seat): Hex | null {
  for (const [k, p] of s.cells) {
    if (p.type === 'heartroot' && p.owner === seat) return parseKey(k);
  }
  return null;
}

/** How many of the six cells around `seat`'s Heartroot are filled, by anyone. */
export function pressureOn(s: GameState, seat: Seat): number {
  const hr = heartroot(s, seat);
  if (!hr) return 0;
  let n = 0;
  for (const nk of neighborKeys(hr)) if (s.cells.has(nk)) n++;
  return n;
}

export function polarityAt(s: GameState, at: Hex, owner: Seat): Polarity {
  let own = 0;
  let foe = 0;
  for (const nk of neighborKeys(at)) {
    const p = s.cells.get(nk);
    if (!p) continue;
    if (p.owner === owner) own++;
    else foe++;
  }
  return foe > own ? 'reaching' : 'nested';
}

/** A piece adjacent to an enemy Warden cannot move at all. */
export function isPinned(s: GameState, at: Hex, owner: Seat): boolean {
  for (const nk of neighborKeys(at)) {
    const p = s.cells.get(nk);
    if (p && p.owner !== owner && p.type === 'warden') return true;
  }
  return false;
}

// ── move generation ─────────────────────────────────────────────────────────

/**
 * Slide destinations, walking the rim of the colony with the piece LIFTED.
 *
 * `occ` must already exclude the moving piece: a slide is a piece travelling
 * around the outside of the colony it is no longer part of, and leaving it in
 * would let it use itself as a handrail.
 */
function slideDests(
  occ: Set<HexKey>,
  from: Hex,
  steps: number,
  exact: boolean,
  squeeze: boolean,
): Hex[] {
  const out = new Map<HexKey, Hex>();
  const path = new Set<HexKey>([key(from)]);

  const walk = (at: Hex, depth: number): void => {
    if (depth > 0 && (!exact || depth === steps)) {
      const k = key(at);
      if (k !== key(from)) out.set(k, at);
    }
    if (depth === steps) return;
    for (const d of DIRS) {
      const to = add(at, d);
      const tk = key(to);
      if (occ.has(tk) || path.has(tk)) continue;
      const [g1, g2] = gateCells(at, to);
      const o1 = occ.has(key(g1));
      const o2 = occ.has(key(g2));
      if (!o1 && !o2) continue; // would break contact with the colony
      if (!squeeze && o1 && o2) continue; // too tight to squeeze between
      path.add(tk);
      walk(to, depth + 1);
      path.delete(tk);
    }
  };

  walk(from, 0);
  return [...out.values()];
}

/**
 * Spindle jumps: leap in a straight line over a contiguous run of pieces and
 * land on the first empty cell beyond it. Nested, it may only clear a single
 * piece; reaching, it clears a line of any length — which is how a Spindle
 * crosses the whole colony in one turn, but only from a position it had to
 * expose itself to reach.
 */
function jumpDests(occ: Set<HexKey>, from: Hex, maxOver: number): Hex[] {
  const out: Hex[] = [];
  for (const d of DIRS) {
    let cur = add(from, d);
    let over = 0;
    while (occ.has(key(cur))) {
      over++;
      cur = add(cur, d);
    }
    if (over >= 1 && over <= maxOver) out.push(cur);
  }
  return out;
}

/** Where `piece` standing at `from` may go, given whose turn it is. */
export function destinationsFor(s: GameState, from: Hex): Hex[] {
  const p = s.cells.get(key(from));
  if (!p || p.owner !== s.toMove) return [];
  if (p.type === 'heartroot' || p.type === 'sporeling') return [];
  if (isPinned(s, from, p.owner)) return [];

  const occ = occupiedSet(s);
  if (cutCells(occ).has(key(from))) return []; // lifting it would split the colony
  occ.delete(key(from));

  const pol = polarityAt(s, from, p.owner);
  if (p.type === 'spindle') {
    return jumpDests(occ, from, pol === 'nested' ? 1 : Number.MAX_SAFE_INTEGER);
  }
  const gait = GAITS[p.type];
  return slideDests(occ, from, gait[pol], gait.exact, gait.squeeze);
}

/**
 * Cells a NEW piece of this type may be placed on.
 *
 * The Sporeling is deliberately exempt from the own-colour rule. Without the
 * exemption it would be unplayable in Thicket and Deadwood: those modes forbid
 * placing against the enemy, and a Sporeling that cannot touch an enemy piece
 * can never convert one, so its only ability would be unreachable in two modes
 * out of three.
 */
export function placementsFor(s: GameState, type: PieceType): Hex[] {
  const seat = s.toMove;
  if (s.hands[seat][type] <= 0) return [];
  if (s.hands[seat].heartroot > 0 && type !== 'heartroot') return [];

  if (s.cells.size === 0) return [ORIGIN];

  const anchorAny = s.mode.freePlacement || type === 'sporeling' || s.cells.size === 1;
  const cand = new Map<HexKey, Hex>();
  for (const [k, p] of s.cells) {
    if (!anchorAny && p.owner !== seat) continue;
    for (const d of DIRS) {
      const c = add(parseKey(k), d);
      const ck = key(c);
      if (!s.cells.has(ck)) cand.set(ck, c);
    }
  }

  const out: Hex[] = [];
  for (const [ck, c] of cand) {
    let touchesOwn = false;
    let touchesFoe = false;
    for (const nk of neighborKeys(c)) {
      const p = s.cells.get(nk);
      if (!p) continue;
      if (p.owner === seat) touchesOwn = true;
      else touchesFoe = true;
    }
    if (!touchesOwn && !touchesFoe) continue;
    if (!anchorAny && (!touchesOwn || touchesFoe)) continue;
    void ck;
    out.push(c);
  }
  return out;
}

/** Enemy pieces a Sporeling landing on `at` would be able to convert. */
export function convertTargets(s: GameState, at: Hex): Hex[] {
  const out: Hex[] = [];
  for (const nk of neighborKeys(at)) {
    const p = s.cells.get(nk);
    if (p && p.owner !== s.toMove && p.type !== 'heartroot') out.push(parseKey(nk));
  }
  return out;
}

export function legalActions(s: GameState): Action[] {
  if (s.over) return [];
  const seat = s.toMove;
  const out: Action[] = [];

  // Your Heartroot is your first placement, always.
  //
  // A "place it by your Nth piece" variant was built and swept (1/2/3/4) and
  // returned BYTE-IDENTICAL results at every setting, because the search never
  // voluntarily delays its Heartroot — so the rule never fired and the sweep
  // measured nothing. That is not evidence the rule is neutral, it is evidence
  // it is untested, so it was removed rather than shipped unmeasured.
  if (s.hands[seat].heartroot > 0) {
    for (const to of placementsFor(s, 'heartroot')) out.push({ k: 'place', piece: 'heartroot', to });
    return out.length ? out : [{ k: 'pass' }];
  }

  for (const type of PIECE_ORDER) {
    if (type === 'heartroot' || s.hands[seat][type] <= 0) continue;
    for (const to of placementsFor(s, type)) {
      if (type === 'sporeling') {
        const targets = convertTargets(s, to);
        if (targets.length === 0) out.push({ k: 'place', piece: type, to });
        // A Sporeling that CAN convert must — it is not a wall with an optional
        // ability, it is the ability.
        else for (const c of targets) out.push({ k: 'place', piece: type, to, convert: c });
      } else {
        out.push({ k: 'place', piece: type, to });
      }
    }
  }

  const occ = occupiedSet(s);
  const cuts = cutCells(occ);
  for (const [k, p] of s.cells) {
    if (p.owner !== seat) continue;
    if (p.type === 'heartroot' || p.type === 'sporeling') continue;
    if (cuts.has(k)) continue;
    const from = parseKey(k);
    if (isPinned(s, from, seat)) continue;
    for (const to of destinationsFor(s, from)) out.push({ k: 'move', from, to });
  }

  return out.length ? out : [{ k: 'pass' }];
}

export function sameAction(a: Action, b: Action): boolean {
  if (a.k !== b.k) return false;
  if (a.k === 'pass') return true;
  if (a.k === 'place' && b.k === 'place') {
    return (
      a.piece === b.piece &&
      a.to.q === b.to.q &&
      a.to.r === b.to.r &&
      (a.convert?.q ?? null) === (b.convert?.q ?? null) &&
      (a.convert?.r ?? null) === (b.convert?.r ?? null)
    );
  }
  if (a.k === 'move' && b.k === 'move') {
    return a.from.q === b.from.q && a.from.r === b.from.r && a.to.q === b.to.q && a.to.r === b.to.r;
  }
  return false;
}

export function isLegal(s: GameState, a: Action): boolean {
  return legalActions(s).some((l) => sameAction(l, a));
}

// ── applying a turn ─────────────────────────────────────────────────────────

/**
 * Apply an action, returning a NEW state. Throws on an illegal action rather
 * than silently diverging — over the wire that is the difference between one
 * peer rejecting a bad packet and two peers playing different games.
 */
export function applyAction(s: GameState, a: Action): GameState {
  if (s.over) throw new Error('game is over');
  if (!isLegal(s, a)) throw new Error(`illegal action: ${JSON.stringify(a)}`);
  return applyUnchecked(s, a);
}

/**
 * The same transition WITHOUT re-deriving the legal list.
 *
 * `isLegal` regenerates every legal action, which is by far the most expensive
 * thing in this file — and the AI search and the balance sim only ever apply
 * actions they just took OUT of `legalActions`, so they would pay for that
 * derivation twice per node. Skipping it took the sim from ~1.5s a game to
 * ~30ms, which is the difference between a balance suite that runs on every
 * commit and one nobody runs.
 *
 * Anything that has NOT just generated the action itself — above all the
 * network — must go through `applyAction` instead. That validation is what
 * makes a hostile or buggy packet a rejection rather than a desync.
 */
export function applyUnchecked(s: GameState, a: Action): GameState {
  const n = cloneState(s);
  if (a.k === 'place') {
    n.cells.set(key(a.to), { type: a.piece, owner: s.toMove });
    n.hands[s.toMove][a.piece]--;
    if (a.convert) {
      const victim = n.cells.get(key(a.convert));
      if (victim) n.cells.set(key(a.convert), { ...victim, owner: s.toMove });
    }
    n.passes = 0;
  } else if (a.k === 'move') {
    const p = n.cells.get(key(a.from))!;
    n.cells.delete(key(a.from));
    n.cells.set(key(a.to), p);
    n.passes = 0;
  } else {
    n.passes = s.passes + 1;
  }

  n.history.push(a);
  n.ply = s.ply + 1;
  n.toMove = other(s.toMove);
  // The second player's opening seeds twice — the measured compensation for
  // first-player tempo (BALANCE.md). Expressed as "seat 1 does not yield the
  // turn after placing its Heartroot", so it costs no extra state and cannot
  // desync: every peer derives it from the ply number alone.
  if (s.mode.openingBonus && s.toMove === 1 && s.ply === 1) n.toMove = 1;
  settle(n);
  return n;
}

/**
 * How many occupied cells choke a Heartroot RIGHT NOW.
 *
 * Falls by one at each ply in `mode.wither`, never below three. This is the
 * whole of the wither: no stored state, no message, no sync surface — every
 * peer, the AI and the sim derive it from the ply counter they already agree
 * on. See BALANCE.md for why the shape is "small early, big late".
 */
export function ringNeeded(mode: Mode, ply: number, seat: Seat): number {
  let t = mode.ringThreshold;
  // The second player's Heartroot withers later — the measured compensation for
  // moving second. The elder root rots first.
  const shift = seat === 0 ? 0 : mode.witherSkew;
  for (const w of mode.wither) if (ply >= w + shift) t--;
  return Math.max(3, t);
}

/** Resolve win / draw / cap after a turn. */
function settle(n: GameState): void {
  const p0 = pressureOn(n, 0);
  const p1 = pressureOn(n, 1);
  const dead0 = heartroot(n, 0) != null && p0 >= ringNeeded(n.mode, n.ply, 0);
  const dead1 = heartroot(n, 1) != null && p1 >= ringNeeded(n.mode, n.ply, 1);

  // Both choked at once. `n.toMove` has already flipped, so the player who
  // actually closed the ring is the OTHER one.
  if (dead0 && dead1) {
    return end(n, n.mode.doubleRing === 'mover' ? other(n.toMove) : null, 'ring');
  }
  if (dead0) return end(n, 1, 'ring');
  if (dead1) return end(n, 0, 'ring');

  // Neither side can act, or we hit the cap: resolve on pressure rather than
  // letting a surround game dribble out as a shrug. Whoever has crowded the
  // enemy Heartroot harder has been winning, and now wins.
  if (n.passes >= 2) return end(n, byPressure(n, p0, p1), 'stalemate');
  if (n.ply >= n.mode.turnCap) return end(n, byPressure(n, p0, p1), 'pressure');
}

/**
 * The countback, in order: pressure, then commitment, then the seat rule.
 *
 * The middle step exists because the seat rule was doing all the work and it is
 * arbitrary. In Bramble, `tieToSecond` was not correcting a bias — it WAS the
 * bias: the mode splits 36/32/32 on its own, and handing every one of those
 * draws to seat 1 manufactured a 36/64 rout out of a nearly even game.
 * Commitment is seat-neutral and play-based: the player who has committed more
 * of their hand to the board has been playing the position rather than sitting
 * on it, and gets the nod ahead of any coin.
 */
function byPressure(n: GameState, p0: number, p1: number): Seat | null {
  if (p1 > p0) return 0;
  if (p0 > p1) return 1;
  const inHand0 = handSize(n.hands[0]);
  const inHand1 = handSize(n.hands[1]);
  if (inHand0 < inHand1) return 0;
  if (inHand1 < inHand0) return 1;
  return n.mode.tieToSecond ? 1 : null;
}

function end(n: GameState, winner: Seat | null, reason: EndReason): void {
  n.over = true;
  n.winner = winner;
  n.reason = reason;
}

export function resign(s: GameState, seat: Seat): GameState {
  const n = cloneState(s);
  end(n, other(seat), 'resign');
  return n;
}

/** Invariant the whole game rests on. Asserted in tests, not in the hot path. */
export function colonyIsWhole(s: GameState): boolean {
  return isConnected(occupiedSet(s));
}

// ── wire format ─────────────────────────────────────────────────────────────

/**
 * Compact wire form. Lockstep sends only this — five small ints at most — so a
 * turn is a few dozen bytes and there is nothing in it that could disagree with
 * the receiver's board.
 */
export type WireAction =
  | [0] // pass
  | [1, number, number, number, number, number] // place: pieceIdx, q, r, convQ, convR (NaN-free sentinel below)
  | [2, number, number, number, number]; // move: fromQ, fromR, toQ, toR

const NO_CONVERT = 0x7fff;

export function encodeAction(a: Action): WireAction {
  if (a.k === 'pass') return [0];
  if (a.k === 'place') {
    return [
      1,
      PIECE_ORDER.indexOf(a.piece),
      a.to.q,
      a.to.r,
      a.convert ? a.convert.q : NO_CONVERT,
      a.convert ? a.convert.r : NO_CONVERT,
    ];
  }
  return [2, a.from.q, a.from.r, a.to.q, a.to.r];
}

export function decodeAction(w: unknown): Action | null {
  if (!Array.isArray(w) || w.length === 0) return null;
  const nums = w as number[];
  if (!nums.every((n) => typeof n === 'number' && Number.isInteger(n))) return null;
  if (nums[0] === 0 && nums.length === 1) return { k: 'pass' };
  if (nums[0] === 1 && nums.length === 6) {
    const piece = PIECE_ORDER[nums[1]];
    if (!piece) return null;
    const convert = nums[4] === NO_CONVERT ? undefined : { q: nums[4], r: nums[5] };
    return { k: 'place', piece, to: { q: nums[2], r: nums[3] }, convert };
  }
  if (nums[0] === 2 && nums.length === 5) {
    return { k: 'move', from: { q: nums[1], r: nums[2] }, to: { q: nums[3], r: nums[4] } };
  }
  return null;
}
